// deps
import React, { memo, useState, useRef } from "react";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// internal-deps
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { LottieAnimation, getCloudinaryUrl, useEffectOnce } from "@swiggy-private/core-ui";

// Constants
import {
    BOOSTER_LOGO_HEIGHT_WIDTH,
    CALLOUT_ICON_HEIGHT_WIDTH,
    LOTTIE_ANIMATIONS_BOOSTER_PROPS,
    DINEOUT_SHOW_BOOSTER_ANIMATION_STORAGE_KEY,
    BOOSTER_ANIMATION_START_DELAY,
    BOOSTER_ANIMATION_TIMEOUT,
} from "@dineout-cart-ui/constants/index";

// Test IDs
import { REWARDS_WIDGET_TEST_IDS } from "@dineout-cart-ui/helpers/TestIds";
//helper
import { isEmpty } from "@swiggy-private/js-utils";

// Styles
import {
    RewardWidgetBackgroundImage,
    BoosterComponent,
    LogoContainer,
    TextContainer,
    CalloutIcon,
    LottieContainer,
    TitleText,
} from "./styles";
import { useSessionStorage } from "@swiggy-private/checkout-core";

interface IBoosterWidgetProps {
    backgroundImage?: string;
    dinecash2xLogoUrl?: string;
    calloutIconUrl?: string;
    boosterAnimation?: string;
    useRewardTitle?: string;
    headerTitle?: string;
    headerSubtitle?: string;
    children: React.ReactNode;
}

export const BoosterWidget: React.FC<IBoosterWidgetProps> = (props: IBoosterWidgetProps) => {
    const {
        backgroundImage,
        dinecash2xLogoUrl,
        calloutIconUrl,
        boosterAnimation,
        children,
        useRewardTitle,
        headerTitle,
        headerSubtitle,
    } = props;

    const [isAnimationComplete, setIsAnimationComplete] = useState(false);
    const [showAnimation, setShowAnimation] = useSessionStorage(DINEOUT_SHOW_BOOSTER_ANIMATION_STORAGE_KEY, true);
    const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const startAnimationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [shouldStartAnimation, setShouldStartAnimation] = useState(false);
    const handleAnimationComplete = () => {
        setIsAnimationComplete(true);
    };

    useEffectOnce(() => {
        startAnimationTimeoutRef.current = setTimeout(() => {
            setShouldStartAnimation(true);
        }, BOOSTER_ANIMATION_START_DELAY);

        animationTimeoutRef.current = setTimeout(() => {
            setShowAnimation(false);
        }, BOOSTER_ANIMATION_TIMEOUT);

        return () => {
            animationTimeoutRef?.current && clearTimeout(animationTimeoutRef.current);
            startAnimationTimeoutRef?.current && clearTimeout(startAnimationTimeoutRef.current);
        };
    });

    return (
        <RewardWidgetBackgroundImage
            backgroundImage={backgroundImage || ""}
            booster={true}
            data-testid={REWARDS_WIDGET_TEST_IDS.BOOSTER_CONTAINER}
        >
            <BoosterComponent>
                <LogoContainer>
                    <Image
                        src={getCloudinaryUrl(dinecash2xLogoUrl)}
                        width={`${BOOSTER_LOGO_HEIGHT_WIDTH}px`}
                        height={`${BOOSTER_LOGO_HEIGHT_WIDTH}px`}
                        cloudinaryWidth={BOOSTER_LOGO_HEIGHT_WIDTH * 2}
                        cloudinaryHeight={BOOSTER_LOGO_HEIGHT_WIDTH * 2}
                    />
                </LogoContainer>
                <TextContainer>
                    {!isEmpty(headerTitle) && (
                        <TitleText
                            variant="V2.overline.neutral.bold_spaced"
                            color="rating_Excellent"
                            data-testid={REWARDS_WIDGET_TEST_IDS.BOOSTER_TITLE}
                        >
                            {headerTitle}
                        </TitleText>
                    )}
                    {!isEmpty(headerSubtitle) && (
                        <Text
                            variant="V2.body2.neutral.medium"
                            color="rating_Excellent"
                            data-testid={REWARDS_WIDGET_TEST_IDS.BOOSTER_SUBTITLE}
                        >
                            {headerSubtitle}
                        </Text>
                    )}
                </TextContainer>
                {showAnimation && shouldStartAnimation && (
                    <LottieContainer useRewardTitle={useRewardTitle} isAnimationComplete={isAnimationComplete}>
                        <LottieAnimation
                            {...LOTTIE_ANIMATIONS_BOOSTER_PROPS}
                            fileName={getCloudinaryUrl(boosterAnimation)}
                            visible={!isAnimationComplete}
                            playLottie={true}
                            onPlayComplete={handleAnimationComplete}
                        />
                    </LottieContainer>
                )}
                <CalloutIcon>
                    <Image src={getCloudinaryUrl(calloutIconUrl)} width={`${CALLOUT_ICON_HEIGHT_WIDTH}`} />
                </CalloutIcon>
            </BoosterComponent>
            {children}
        </RewardWidgetBackgroundImage>
    );
};

export default memo(BoosterWidget);
